import React from 'react';
import { ControlLink, Stack, Text } from '@moda/om';
import { Link } from 'react-router-dom';
import { PageMeta } from '../../components/PageMeta';
import { hrefFor } from '../../routers';
import { useScrollHashReferenceToView } from '../../hooks/useScrollHashReferenceToView';

import './PrivacyPolicyPage.scss';

const YOUR_RIGHTS_ID = 'your-rights-and-choices';

const PrivacyPolicyPageYourRights = () => (
  <>
    "
    <ControlLink className="PrivacyPolicyPage__link" href={`#${YOUR_RIGHTS_ID}`}>
      Your Rights and Choices
    </ControlLink>
    "
  </>
);

export const PrivacyPolicyPage = () => {
  useScrollHashReferenceToView();
  return (
    <>
      <PageMeta title="Privacy Policy" />
      <div className="PrivacyPolicyPage">
        <div className="PrivacyPolicyPage__title">
          <Text treatment="h3">PRIVACY POLICY</Text>
        </div>

        <Stack space={4}>
          <Text treatment="eyebrow" className="PrivacyPolicyPage__update">
            Last Updated: December 23, 2025
          </Text>
          <Text treatment="body1">
            Moda Operandi, Inc. (together with our affiliates and subsidiaries, “Moda”, "we", "us",
            or "our”) created this Privacy Policy to inform you about how we process the information
            we collect from and about you (or on your behalf, if you authorize a third party,
            automated agent, automated shopping tool, or other electronic agent to use the Site on
            your behalf) when you or a third-party acting on your behalf visit our website located
            at www.modaoperandi.com (the "Site"), access our mobile applications ("Mobile App"),
            purchase any of our products ("Products"), visit our showrooms, attend our events, or
            use any of our services, features, or other offerings (collectively, the “Services”).
          </Text>
          <Text>
            By using or accessing our Services, you agree to this Privacy Policy. For questions
            about our Services, or other requests not pertaining to data privacy, please see our{' '}
            <ControlLink className="PrivacyPolicyPage__link" to={hrefFor.TermsAndConditionsPage()}>
              Terms and Conditions
            </ControlLink>
            .
          </Text>

          <Stack className="PrivacyPolicyPage__section" space={4}>
            <Link to="#information-we-collect" className="PrivacyPolicyPage__section-link">
              <Text id="information-we-collect" treatment="h5">
                Information We Collect How We Use It
              </Text>
            </Link>
            <Text treatment="body1">
              We collect and use information as described in this Privacy Policy.
            </Text>
            <Text treatment="bold1">Information We Collect Directly From You</Text>
            <Text treatment="bold1">Contact and Registration Information</Text>
            <Text treatment="body1">
              We collect certain information on our Services from visitors and customers who wish to
              obtain information or support, purchase our Products, or use our Services (e.g.,
              booking a Showroom or Customer Acquisition Appointment). Information we collect may
              include your username and password, and contact information, such as, first name, last
              name, shipping address, telephone number, and email address ("Registration
              Information"), or other information that you submit to us. Registration Information is
              used to establish an account, and/or provide the requested Product, Services, or
              information, and to contact prospective and current customers for purposes of direct
              marketing of our current and future products and services. In accordance with
              applicable legal requirements, when you request and/or agree to receive marketing
              emails, newsletters, or similar communications, or when you have purchased Products or
              Services, or expressed interest in such Products or Services (e.g., by attending a
              showroom or customer acquisition appointment), we will use your Registration
              Information for purposes of direct marketing of our current and future Services, and
              to send you communications via email or SMS, such as for marketing or information and
              operational purposes (including, but not limited to, account management, customer
              service, or system maintenance).
            </Text>
            <Text treatment="body1">
              If you authorize us to send you text messages, we will not sell or disclose that
              consent to third parties so that third parties can send you their own promotional or
              marketing texts. We otherwise do not sell or disclose your mobile phone number to
              third parties for their own promotional or marketing purposes except as directed by
              you.
            </Text>

            <Text treatment="bold1">Billing Information</Text>
            <Text treatment="body1">
              If you wish to purchase a Product or Service through the Services, you will be
              required to provide information which may include a billing address, payment method,
              credit card number, expiration date, card verification value, and similar information
              ("Billing Information"). We directly collect and use Billing Information to support
              our checkout process and improve our Services, such as personalizing your checkout
              process and options. Billing Information is collected and processed by our third-party
              payment processor operating as our agent.
            </Text>

            <Text treatment="bold1">Customer Purchase and Browsing Data</Text>
            <Text treatment="body1">
              We collect and use the information we have about your past purchases and browsing
              activity to create customer profiles and deliver personalized user experiences and
              product recommendations.
            </Text>

            <Text treatment="bold1">
              Customer Relationship Data Customer Submitted Information
            </Text>
            <Text treatment="body1">
              We use an internal customer relationship management (“CRM”) system to access and use
              order history information as well as Product and interaction preferences. Our private
              client advisors or our other team members may also request and receive characteristics
              information about you, such as your measurements, style and fit preferences, birthday,
              and other communications between you and Moda Operandi or those acting on our behalf
              (collectively, “Customer Relationship Data”) to help us provide you with our Services.
              Any information provided to/or collected by us for these purposes will be used to
              provide our Services in accordance with this Privacy Policy.
            </Text>

            <Text treatment="body1">
              We also collect information that you submit to us, such as when you communicate with
              our Care team, private client advisors, or other Moda employees, representatives,
              agents, or third parties providing services on our behalf. This includes information
              that you submit when you engage in our online customer service chat functionality.
              These chats may be recorded and stored by us and our third-party chat service
              providers for quality and assurance purposes. In addition, from time to time we may
              contact you to participate in optional surveys—if you do decide to participate, you
              may be asked to provide certain information, which may include contact details and
              questions about your preferences.
            </Text>

            <Text treatment="bold1">Information We Collect Passively</Text>
            <Text treatment="body1">
              When you use our Services, we may passively collect a variety of information about
              you, your devices, and your interactions with our Services.
            </Text>

            <Text treatment="bold1">Logging</Text>
            <Text treatment="body1">
              Like many online and digital services, we (or third parties acting on our behalf)
              automatically receive and record certain information about visitors and users of our
              Services. Our servers and IT applications automatically collect certain information on
              traffic, usage and patterns, and store it in log files. The information we collect for
              this purpose includes IP addresses, user agent information (such as your browser
              type), internet service provider, entry/exit pages, date and time stamps, clickstream
              information, and other session activity. We analyze this information so we can better
              understand trends, administer and improve the Services, and make certain decisions in
              the interest of our customers.
            </Text>

            <Text treatment="bold1">Cookies</Text>
            <Text treatment="body1">
              We use cookies and similar technologies to help us collect information about your use
              of our Services. Cookies are small files sent to your device by us that help us better
              tailor our Services to you. A cookie is unique to your browser. Some cookies are
              session-based and only last while your browser is open, while others are persistent
              and last until you or your browser deletes them.
            </Text>

            <Text treatment="body1">
              We use cookies for a number of reasons to help us better personalize your experience
              and improve our Services. Some cookies may be necessary for us to provide you with
              certain features or the Services that you request, such as logging into your
              account—if you disable these cookies, certain features or Services may not function as
              expected. We also may use cookies to record some of your choices and settings, which
              help us personalize your experience with our Services (for example, remembering your
              name on our Site or Mobile App). Some of the cookies from our Site and Mobile App come
              from companies that help us process data or provide us with certain services, such as
              performance and analytical insights. For more information about our use of third-party
              cookies, please see the "
              <ControlLink className="PrivacyPolicyPage__link" href="#analytics-information">
                Information Collected by Third Parties
              </ControlLink>
              " section below. To learn more about cookies and how to manage or delete them, please
              visit{' '}
              <ControlLink
                className="PrivacyPolicyPage__link"
                href="https://www.allaboutcookies.org/"
              >
                https://www.allaboutcookies.org/
              </ControlLink>
            </Text>

            <Text treatment="body1">
              In addition, we use cookies to help keep track of items you put into your shopping
              cart, including when you have abandoned your cart, and this information is used to
              determine when to send cart reminder messages via SMS.
            </Text>

            <Text treatment="body1">
              We may also use web beacons or other technologies to help us manage content on the
              Services, for example, to track what content is most interacted with. We may use these
              technologies to track online movement of our customers and visitors/users to our Site
              or Mobile App, but in contrast to cookies (which are stored on a local hard drive),
              these technologies are embedded in our Site or the Mobile App. We may also use these
              technologies in our emails to see what emails are opened so we can gauge the
              effectiveness of certain communications, such as our marketing campaigns.
            </Text>

            <Text treatment="bold1">De-Identified Data</Text>
            <Text treatment="body1">
              In an ongoing effort to better understand our users and usage of our Services, we may
              de-identify the information we collect from and about you so that such information can
              no longer be tied to you personally (“De-Identified Data”). We may analyze, use, or
              disclose such De-Identified Data for any purpose, including, but not limited to,
              operating, maintaining, managing, and improving our Products and Services, marketing,
              and disclosing such information to our agents, business partners, affiliates, or any
              other third party. We may also disclose De-Identified Data and related user statistics
              in order to describe our Services to current and prospective business partners and to
              other third parties for other lawful purposes.
            </Text>

            <Text treatment="bold1">Internal and Services-Related Usage</Text>
            <Text treatment="body1">
              We use your information to continue improving and developing Products and Services
              that we may offer you, and to optimize your experience with us. We use and retain the
              information we collect to analyze, improve, and facilitate the development of our
              Services, including: to analyze interactions with and performance of our Services; to
              better understand your use of the Services and of our customers and visitors; to debug
              and identify errors that impair functionality; to detect security incidents, protect
              against malicious, deceptive, fraudulent, or illegal activity; for research and
              development; and to improve, supplement, or enhance our Services, or develop new
              products and services.
            </Text>

            <Text treatment="bold1">Displaying to Other Customers or Users</Text>
            <Text treatment="body1">
              Any content that you post in a publicly viewable forum on our Services (for example,
              in a public chat, forum, or in a Product review) may be publicly displayed on our
              Site, Mobile App, or otherwise through our Services and viewable by third parties, who
              may be able to see some or all of the content that you post and other information
              about you, such as your name or username that you use to post such information. Moda
              is not responsible for the content that you post or the privacy practices of other
              visitors or customers who may view and use information that you post in such public
              forums.
            </Text>

            <Text treatment="bold1">Information From Third Parties</Text>
            <Text treatment="body1">
              We may collect information about you from third parties or supplement the information
              we collect from you with additional records received from third parties. We also
              collect information from third parties to enhance our ability to serve you, to tailor
              our content to you, to offer you information that we believe may be of interest to
              you, and to inform our commercial and operational decisions.
            </Text>
          </Stack>

          <Stack className="PrivacyPolicyPage__section" space={4}>
            <Link to="#analytics-information" className="PrivacyPolicyPage__section-link">
              <Text id="analytics-information" treatment="h5">
                Analytics Information
              </Text>
            </Link>
            <Text treatment="body1">
              We use third-party service providers on the Site and the Mobile App (such as Google
              Analytics and Google Optimize) to evaluate your use of the Site and Mobile App,
              compile reports on activity (based on the collection of IP addresses, Internet service
              provider, browser type, operating system and language, referring and exit pages and
              URLs, date and time, amount of time spent on particular pages, what sections of the
              Site and Mobile App you visit, number of links clicked, search terms, and other
              similar usage data), and/or analyze performance metrics. These parties may use cookies
              and other technologies to help collect, analyze and provide us with reports and/or
              data.
            </Text>

            <Text treatment="body1">
              For more information on these third parties, including how to opt-out from certain
              data collection, please review the section on <PrivacyPolicyPageYourRights /> below.
            </Text>

            <Text treatment="bold1">Remarketing Advertising</Text>

            <Text treatment="body1">
              We partner with third-party advertising companies and marketing organizations (“Ad
              Network Partners”) to either display advertising on our Site or Mobile App which we
              believe will be of interest to you, or to display our advertising on other sites or
              apps. Our Ad Network Partners use cookies and web beacons (for example, pixels) and
              may collect certain data typically associated with online identifiers, including your
              email, email hash, user ID, session ID, cookie ID, browser type, mobile device,
              referring URL, member ID, city, country, demographic data, and information about your
              activities on our Site or Mobile App and other web sites and apps to provide you
              targeted advertising based upon your consumer profile. Please see the{' '}
              <PrivacyPolicyPageYourRights /> section below for more information on how to opt-out.
            </Text>

            <Text treatment="bold1">Social Media Platforms and Interactive Features</Text>
            <Text treatment="body1">
              Our Services may include or contain certain social media plug-ins or other interactive
              features provided by various third-party platforms, such as the ability to post to
              Facebook, create a Pinterest board, or view and engage in a live-streamed event. These
              features may collect your IP address, which page you are visiting on our Site, and may
              set a cookie (or use similar technologies) to enable the feature to function properly.
              These features are either hosted by a third party or hosted directly on our Site. Your
              interactions with these features are governed by the privacy policies of the companies
              providing them. If you already use these features or platforms, their cookies may be
              set on your device when using the Services.
            </Text>

            <Text treatment="bold1">Legal Bases for Processing</Text>
            <Text treatment="body1">
              The laws in some jurisdictions require companies to tell you about the legal grounds
              they rely on to process your information. Our{' '}
              <Text treatment="bold1">legal bases</Text> for processing your information as
              described in this Privacy Policy are as follows:
            </Text>
            <Text treatment="body1">
              <ul className="PrivacyPolicyPage__list">
                <li>
                  Where use of your information is necessary to perform our obligations under a
                  contract or commitment to you. For example, to provide the services you’ve
                  requested from us, or to comply with our Terms Conditions.
                </li>
                <li>
                  Where use of your information furthers our legitimate interests or the legitimate
                  interests of others. For example, to provide security for our Services, operate
                  our business and our Services, make and receive payments, defend our legal rights,
                  and prevent fraud.
                </li>
                <li>
                  Where we use your information to comply with applicable legal obligations. For
                  example, keeping track of purchases for tax and auditing purposes.
                </li>
                <li>
                  Where you have consented to our processing of your information for a particular
                  purpose.
                </li>
              </ul>
            </Text>
            <Text treatment="body1">
              For the purposes discussed in this Privacy Policy, we may combine the information that
              we collect through the Services with information that we receive from other sources,
              both online and offline, and use such combined information in accordance with this
              Privacy Policy.
            </Text>
          </Stack>

          <Stack className="PrivacyPolicyPage__section" space={4}>
            <Link
              to="#how-we-disclose-your-information"
              className="PrivacyPolicyPage__section-link"
            >
              <Text id="how-we-disclose-your-information" treatment="h5">
                How We Disclose Your Information
              </Text>
            </Link>
            <Text treatment="body1">
              We disclose your information as discussed in this section.
            </Text>

            <Text treatment="bold1">Service Providers</Text>
            <Text treatment="body1">
              We may engage other companies to perform services on our behalf, and we may disclose
              information to them for use in providing these services, such as research services and
              certain business-related functions, database management/backup services, maintenance,
              security, data analytics, communications, shipping, logistics, and fulfillment, mail
              centers, promotional and marketing purposes, sweepstakes and contests, advertising,
              payment processing services, customer service and online chat functionality providers,
              data hosting services, IT support services, and sales support companies.
            </Text>

            <Text treatment="bold1">Brand Partners</Text>
            <Text treatment="body1">
              In accordance with applicable legal requirements, we may disclose your personal
              information to our brand partners so that they may provide you with promotions and
              offers that may be of interest to you. To opt out of such disclosure, please see the{' '}
              <PrivacyPolicyPageYourRights /> section below.
            </Text>

            <Text treatment="bold1">Our Affiliates</Text>
            <Text treatment="body1">
              We may disclose your information to our affiliates, which may include a parent
              company, subsidiaries, joint venture parties, or other companies that we control or
              are under common control with us, in which case their use of your information will be
              treated in accordance with this Privacy Policy.
            </Text>

            <Text treatment="bold1">Public Authorities</Text>
            <Text treatment="body1">
              We may access, preserve, and disclose information if we believe in good faith that
              doing so is required or appropriate to comply with laws, regulations, law enforcement
              requests, and to comply with other legal processes, such as a court order, government
              request, or subpoena, including for the purpose of meeting national security or law
              enforcement requirements.
            </Text>

            <Text treatment="bold1">Protecting Our Rights</Text>
            <Text treatment="body1">
              We may also use or disclose information where appropriate or necessary for us to:
              prevent or address violations of the Terms and Conditions, this Privacy Policy, or our
              other agreements; protect our, your, or others' rights and/or property, or safety;
              maintain and protect the security and integrity of our Products and Services or
              infrastructure; protect ourselves and our Services from fraudulent, abusive, or
              unlawful uses; or investigate and defend ourselves against claims or allegations.
            </Text>

            <Text treatment="bold1">Online Advertising Partners</Text>
            <Text treatment="body1">
              As discussed above in the "Remarketing Advertising" section, we may disclose
              certain information about you to our Ad Network Partners to assist us in our online
              advertising and measurement activities.
            </Text>

            <Text treatment="bold1">Business Transfers</Text>
            <Text treatment="body1">
              As we develop our business, we might sell or buy businesses or some or all of our
              assets. In the event of a corporate sale, merger, reorganization, dissolution, or
              similar event, we expect that information that we have collected, including
              information from and about you, would be transferred to one or more surviving or
              acquiring entities. All such transfers shall be subject to our commitments with
              respect to the privacy as set forth in this Privacy Policy. This Privacy Policy shall
              be binding upon Moda Operandi and its legal successors in interest.
            </Text>

            <Text treatment="bold1">Retention of Personal Information</Text>
            <Text treatment="body1">
              We will retain information for as long as it serves the purpose(s) for which it was
              initially collected as stated in this Privacy Policy, subsequently authorized, as
              necessary or appropriate to provide the Services, and for business continuity
              purposes. We also retain information as permitted or required under applicable law to
              comply with applicable law, to resolve disputes, and to enforce our rights and
              agreements.
            </Text>
          </Stack>

          <Stack className="PrivacyPolicyPage__section" space={4}>
            <Link to="#your-rights-and-choices" className="PrivacyPolicyPage__section-link">
              <Text id={YOUR_RIGHTS_ID} treatment="h5">
                Your Rights and Choices
              </Text>
            </Link>

            <Text treatment="bold1">Rights Regarding the Information we Process</Text>

            <Text treatment="body1">
              Depending on your geographic location or place of residence, (which currently includes
              the following U.S. states: California, Colorado, Connecticut, Delaware, Indiana, Iowa,
              Kentucky, Maryland, Minnesota, Montana, Nebraska, New Hampshire, New Jersey, Oregon,
              Rhode Island, Tennessee, Texas, Utah and Virginia), as well as the EU and UK, you may
              have certain statutory rights in relation to your information. Specifically, you may
              be able to request the following:
            </Text>
            <Text treatment="body1">
              <ul className="PrivacyPolicyPage__list">
                <li>
                  Inform you about the categories of information we collect or disclose about you;
                  the categories of sources of such information; the business or commercial purpose
                  for collecting your information; and the categories of third parties with whom we
                  disclose information. For your convenience, this information is already provided
                  in this Privacy Policy.
                </li>
                <li>Access to, or a copy of, your information</li>
                <li>Confirmation that we are processing your information</li>
                <li>Correction or amendment of your information</li>
                <li>Deletion of your information</li>
                <li>Transfer of your information to a third party</li>
                <li>Restriction or objection to certain uses of your information</li>
                <li>
                  Oregon and Minnesota residents can also request a list of the specific third
                  parties, other than natural persons, to which we have disclosed information.
                </li>
              </ul>
            </Text>

            <Text treatment="body1">
              If you reside in one of the U.S. states identified above, you also have the right to
              opt out of “sales” of your information and “sharing/use of your information for
              targeted advertising” as described below. As provided in applicable law, you also have
              the right to not be discriminated or retaliated against for exercising your rights.
            </Text>

            <Text treatment="body1">
              Please note that certain information may be exempt from such requests under applicable
              law. For example, we may not be able to alter or delete certain information if we are
              required to retain it to provide you with Services that you have requested or for us
              to fulfill other obligations to you, for security reasons, to comply with applicable
              law or regulations, or for internal business or other valid reasons. We also need to
              take reasonable steps to verify your identity before responding to a request, which
              may include, at a minimum, depending on the sensitivity of the information you are
              requesting and the type of request you are making, verifying your name and email
              address. You may also be permitted to designate an authorized agent to submit certain
              requests on your behalf. In order for an authorized agent to be verified, you must
              provide the authorized agent with signed, written permission to make such requests or
              a power of attorney. We may also follow up with you to verify your identity before
              processing the authorized agent’s request. If you would like further information
              regarding your legal rights or would like to exercise any of them, please fill out
              this{' '}
              <ControlLink
                className="PrivacyPolicyPage__link"
                href="https://preferences.modaoperandi.com/privacy"
                target="_blank"
              >
                form
              </ControlLink>
              , or call our Customer Care Center at 1 (800) 707-9898 (the “Care Number”). If you are
              a Moda Operandi customer, you may review and make changes to certain of your
              information and related preferences through your customer account.
            </Text>

            <Text treatment="body1">
              If you are a resident of Colorado, Connecticut, Delaware, Iowa, Indiana, Kentucky,
              Maryland, Minnesota, Montana, Oregon, Nebraska, New Hampshire, New Jersey, Rhode
              Island, Tennessee, Texas, or Virginia and we deny your information request, you have
              the right to appeal our denial. You can exercise this right by emailing us at{' '}
              <ControlLink
                className="PrivacyPolicyPage__link"
                href="mailto:privacy@modaoperandi.com"
              >
                privacy@modaoperandi.com
              </ControlLink>{' '}
              with the subject line “Consumer Request Appeal.” Your description must include your
              full name and the email address used for your account with us, along with a copy of
              the denial notice you received from us.
            </Text>

            <Text treatment="bold1">Marketing Emails Communication Preferences</Text>
            <Text treatment="body1">
              You may opt out at any time from the use of your personal information for direct
              marketing purposes by emailing the instructions to{' '}
              <ControlLink
                className="PrivacyPolicyPage__link"
                href="mailto:privacy@modaoperandi.com"
              >
                privacy@modaoperandi.com
              </ControlLink>
              , or by calling the Care Number.
            </Text>
            <Text treatment="body1">
              You may also manage your receipt of marketing communications by clicking on the
              "Unsubscribe" link located on the bottom of any Moda Operandi marketing email and
              following the instructions found on the page to which the link takes you. Note that
              you cannot opt out of receiving transactional emails related to the Services, as well
              as certain communications regarding changes to the Services or other information about
              your use of the Services. You can opt out of the receipt of SMS messages by following
              the instructions contained in those messages.
            </Text>

            <Text treatment="bold1">
              Opting Out of Sales of Personal Information and Processing/Sharing of Personal
              Information for Targeted Advertising Purposes
            </Text>
            <Text treatment="body1">
              As explained in the “Remarketing & Advertising" section above, we sometimes disclose
              information (e.g., usage information, online identifiers, and/or hashed email
              addresses) to Ad Network Partners we collaborate with or that provide online targeted
              advertising that we think may be of value to you, or to assist us with related
              advertising measurement and analytics activities. These disclosures may be considered
              a “sale” or the processing/sharing of personal information for targeted advertising
              purposes under applicable law.
            </Text>
            <Text treatment="body1">
              If you would like to opt out of our online disclosure of your personal information for
              purposes that could be considered “sales” or “sharing” for purposes of targeted
              advertising, please{' '}
              <ControlLink
                className="PrivacyPolicyPage__link"
                href="#"
                onClick={() => window.DG_BANNER_API?.showLayer('new-layer-2')}
              >
                click here.
              </ControlLink>{' '}
              You can also submit a request to opt out of our offline disclosures of information
              that are subject to applicable opt out rights (such as our disclosure of your
              information to brand partners to provide you with relevant marketing as discussed
              above) by{' '}
              <ControlLink
                className="PrivacyPolicyPage__link"
                href="https://preferences.modaoperandi.com/dont_sell"
                target="_blank"
              >
                clicking here
              </ControlLink>{' '}
              or by emailing us at{' '}
              <ControlLink
                className="PrivacyPolicyPage__link"
                href="mailto:privacy@modaoperandi.com"
              >
                privacy@modaoperandi.com
              </ControlLink>
              .
            </Text>
            <Text treatment="body1">
              Please note that if you have a legally-recognized browser-based opt out preference
              signal turned on via your device browser (e.g., Global Privacy Control), we recognize
              such preference in accordance with applicable law.
            </Text>

            <Text treatment="bold1">Privacy Information for California Residents</Text>
            <Text treatment="body1">
              If you are a California resident, the California Consumer Privacy Act (“CCPA”)
              requires us to provide you with the following additional information about: (1) the
              purpose for which we use each category of “personal information” (as defined in the
              CCPA) we collect; and (2) the categories of third parties to which we (a) disclose
              such personal information for a business purpose, (b) “share” personal information for
              “cross-context behavioral advertising,” and/or (c) “sell” such personal information.
              Under the CCPA, “sharing” is defined as the targeting of advertising to a consumer
              based on that consumer's personal information obtained from the consumer's activity
              across websites, and “selling” is defined as the disclosure of personal information to
              third parties in exchange for monetary or other valuable consideration. We “share”
              information to provide more relevant and tailored advertising to you regarding our
              Services. Our use of third-party analytics services and online advertising services
              may result in the sharing of online identifiers (e.g., cookie data, IP addresses,
              device identifiers, and usage information) in a way that may be considered a “sale”
              under the CCPA. The following chart details our practices in this regard. For all
              categories of third parties to which we disclose personal information for a business
              purposes, please see the "How We Disclose Your Information" section of our Privacy
              Policy above.
            </Text>

            <table
              className="PrivacyPolicyPage__table"
              aria-describedby="California Residents CCPA"
            >
              <thead className="PrivacyPolicyPage__thead">
                <tr>
                  <th className="PrivacyPolicyPage__th" scope="col">
                    <p>Category of Personal Information</p>
                  </th>
                  <th className="PrivacyPolicyPage__th" scope="col">
                    <p>Sources of Personal Information</p>
                  </th>
                  <th className="PrivacyPolicyPage__th" scope="col">
                    <p>Business Purpose</p>
                  </th>
                  <th className="PrivacyPolicyPage__th" scope="col">
                    <p>Categories of Consumers</p>
                  </th>
                  <th className="PrivacyPolicyPage__th" scope="col">
                    <p>
                      Categories of Third Parties to Which we "Share" and "Sell" for
                      advertising/analytics purposes
                    </p>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <th className="PrivacyPolicyPage__th" scope="row">
                    <p>
                      Identifiers (anything that can identify a person or device over time,
                      including your name and email)
                    </p>
                  </th>
                  <td className="PrivacyPolicyPage__td">
                    <p>
                      - Information submitted to us by our users or customers, or by our users or
                      customers via our stylists and business partners ("User-submitted
                      information")
                    </p>
                    <p>- Passive data collection</p>
                  </td>

                  <td className="PrivacyPolicyPage__td">
                    <p>- To provide the Services</p>
                    <p>
                      - Business operations (e.g., internal research and analytics, billing and
                      payment processing, account creation, purchase tracking, site design,
                      personalization, and for fraud prevention and security (such as to analyze and
                      detect unauthorized activity) (“Business Operations”)
                    </p>
                    <p>- Advertising and marketing purposes</p>
                    <p>- Legal purposes</p>
                  </td>
                  <td className="PrivacyPolicyPage__td">
                    <p>
                      - Consumers who use our Services (including via our stylists and business
                      partners), consumers who opt-in to our marketing content, consumers who
                      purchase our goods, and consumers who attend our events
                    </p>
                  </td>
                  <td className="PrivacyPolicyPage__td">
                    <p>- With our Ad Network Partners and analytics providers</p>
                  </td>
                </tr>

                <tr>
                  <th className="PrivacyPolicyPage__th" scope="row">
                    <p>
                      Financial and transactional information (e.g., purchase history and billing
                      information)
                    </p>
                  </th>
                  <td className="PrivacyPolicyPage__td">
                    <p>- User-submitted information</p>
                  </td>
                  <td className="PrivacyPolicyPage__td">
                    <p>- To provide the Services,</p>
                    <p>- Business operations</p>
                    <p>
                      - Advertising and marketing purposes (only purchase history, not billing
                      information)
                    </p>
                    <p>- Legal purposes</p>
                  </td>
                  <td className="PrivacyPolicyPage__td">
                    <p>
                      - People that use our site and Services, purchase goods from us, and people
                      who attend our events.
                    </p>
                  </td>
                  <td className="PrivacyPolicyPage__td">
                    <p>- We don't sell/share</p>
                  </td>
                </tr>

                <tr>
                  <th className="PrivacyPolicyPage__th" scope="row">
                    <p>Demographic information (e.g., gender)</p>
                  </th>
                  <td className="PrivacyPolicyPage__td">
                    <p>- User-submitted information</p>
                  </td>
                  <td className="PrivacyPolicyPage__td">
                    <p>- To provide the Services</p>
                    <p>- Business operations</p>
                    <p>- Legal purposes</p>
                  </td>
                  <td className="PrivacyPolicyPage__td">
                    <p>- Anyone that receives marketing content or uses our website</p>
                  </td>
                  <td className="PrivacyPolicyPage__td">
                    <p>- With our Ad Network Partners and analytics providers</p>
                  </td>
                </tr>

                <tr>
                  <th className="PrivacyPolicyPage__th" scope="row">
                    <p>Electronic network activity</p>
                  </th>
                  <td className="PrivacyPolicyPage__td">
                    <p>- User-submitted information</p>
                    <p>- Passive data collection</p>
                  </td>
                  <td className="PrivacyPolicyPage__td">
                    <p>- To provide the Services</p>
                    <p>- Business operations</p>
                    <p>- Advertising and marketing purposes</p>
                    <p>- Legal purposes</p>
                  </td>
                  <td className="PrivacyPolicyPage__td">
                    <p>
                      - Visitors to our Site and Mobile App, and customers that purchase our goods
                    </p>
                  </td>
                  <td className="PrivacyPolicyPage__td">
                    <p>- With our Ad Network Partners and analytics providers</p>
                  </td>
                </tr>

                <tr>
                  <th className="PrivacyPolicyPage__th" scope="row">
                    <p>Geolocation data derived from your IP address</p>
                  </th>
                  <td className="PrivacyPolicyPage__td">
                    <p>- Passive data collection</p>
                  </td>
                  <td className="PrivacyPolicyPage__td">
                    <p>- To provide the Services,</p>
                    <p>- Business operations</p>
                    <p>- Advertising and marketing purposes</p>
                    <p>- Legal purposes</p>
                  </td>
                  <td className="PrivacyPolicyPage__td">
                    <p>- People who use our Services</p>
                  </td>
                  <td className="PrivacyPolicyPage__td">
                    <p>- With our Ad Network Partners and analytics providers</p>
                  </td>
                </tr>

                <tr>
                  <th className="PrivacyPolicyPage__th" scope="row">
                    <p>
                      Sensory Information (audio, electronic, visual, thermal, olfactory or similar)
                    </p>
                  </th>
                  <td className="PrivacyPolicyPage__td">
                    <p>
                      - User-submitted information, such as calls to our customer care phone number
                    </p>
                  </td>
                  <td className="PrivacyPolicyPage__td">
                    <p>- To provide the Services,</p>
                    <p>- Business operations</p>
                    <p>- Legal purposes</p>
                  </td>
                  <td className="PrivacyPolicyPage__td">
                    <p>- Consumers who call us directly</p>
                  </td>
                  <td className="PrivacyPolicyPage__td">
                    <p>- We don't sell/share</p>
                  </td>
                </tr>

                <tr>
                  <th className="PrivacyPolicyPage__th" scope="row">
                    <p>Inferences drawn from any personal information collected</p>
                  </th>
                  <td className="PrivacyPolicyPage__td">
                    <p>- User-submitted information</p>
                    <p>- Passive data collection</p>
                  </td>
                  <td className="PrivacyPolicyPage__td">
                    <p>- To provide the Services,</p>
                    <p>- Business operations</p>
                    <p>- Advertising and marketing purposes</p>
                    <p>- Legal puerposes</p>
                  </td>
                  <td className="PrivacyPolicyPage__td">
                    <p>
                      - Consumers who use our Services, and Consumers who purchase our Products.
                    </p>
                  </td>
                  <td className="PrivacyPolicyPage__td">
                    <p>- With our Ad Network Partners and analytics providers</p>
                  </td>
                </tr>
              </tbody>
            </table>

            <Text treatment="body1">
              For more information about each category, purpose of use, and the third parties to
              which we disclose or share information, please see the “Information we Collect & How
              We Use It" and “How We Disclose Your Information” sections above.
            </Text>

            <Text treatment="bold1">Your Choices Regarding “Sharing” and “Selling”:</Text>
            <Text treatment="body1">
              You have the right to opt out of our sale/sharing of your personal information for
              purposes of online analytics and advertising by{' '}
              <ControlLink
                className="PrivacyPolicyPage__link"
                href="#"
                onClick={() => window.DG_BANNER_API?.showLayer('new-layer-2')}
              >
                clicking here
              </ControlLink>
              . To opt out of the <Text treatment="bold1">offline</Text> disclosure of your
              information to third parties for these purposes (such as our disclosure of your
              information to brand partners to provide you with relevant marketing as discussed
              above), please fill out our{' '}
              <ControlLink
                className="PrivacyPolicyPage__link"
                href="https://preferences.modaoperandi.com/dont_sell"
                target="_blank"
              >
                online request form
              </ControlLink>
              . Please note that if you have a legally-recognized browser-based opt out preference
              signal turned on via your device browser (e.g., Global Privacy Control), we recognize
              such preference in accordance with applicable law.
            </Text>

            <Text treatment="bold1">Financial Incentives</Text>
            <Text treatment="body1">
              We offer various financial incentives. For example, we may provide discounts, coupons
              and other benefits for customers who sign up to receive our marketing emails. When you
              participate in a financial incentive, we collect personal information from you, such
              as identifiers (like your name and email address) and commercial information (like
              your purchase history). You can opt into a financial incentive by following the
              sign-up or participation instructions provided, and you have the ability to opt-out of
              the financial incentive, or our subsequent use of your personal information in
              connection with a financial incentive, at any time by contacting us at{' '}
              <ControlLink
                className="PrivacyPolicyPage__link"
                href="mailto:privacy@modaoperandi.com"
              >
                privacy@modaoperandi.com
              </ControlLink>
              . In some cases, we may provide additional terms and conditions for a financial
              incentive, which we will present to you when you sign up. The value of your personal
              information will be reasonably related to the value of the offer or discount presented
              to you.
            </Text>

            <Text treatment="bold1">Other CCPA Rights</Text>
            <Text treatment="body1">
              The CCPA also allows you to limit the use or disclosure of your “sensitive personal
              information” (as defined in the CCPA) if your sensitive personal information is used
              for certain purposes. Please note that we do not use or disclose sensitive personal
              information other than for business purposes for which you cannot opt out under the
              CCPA.
            </Text>
            <Text treatment="body1">
              Please see the “Your Rights and Choices” section of our Privacy Policy above for
              information about the additional rights you have with respect to your personal
              information under California law and how to exercise them.
            </Text>

            <Text treatment="bold1">"Do Not Track" Signals</Text>
            <Text treatment="body1">
              At this time, our Site, Mobile App, and Services are not configured to read or respond
              to "do not track" signals from your browser or user agent. Please note that "Do Not
              Track" is a different mechanism than the browser-based privacy signals discussed
              above.
            </Text>

            <Text treatment="bold1">Retention of Your Personal Information</Text>
            <Text treatment="body1">
              Please see the “Retention of Your Information” section below.
            </Text>

            <Text treatment="bold1">“Shine the Light” Disclosure</Text>
            <Text treatment="body1">
              The California “Shine the Light” law gives residents of California the right under
              certain circumstances to request, on a yearly basis, information about our disclosure
              of certain categories of “personal information” (as defined in the Shine the Light
              law) to third parties for their own “direct marketing purposes” (as defined in the
              Shine the Light law), or in the alternative, that we maintain a policy of providing a
              cost-free means for consumers to opt out of any such disclosure. We do not currently
              disclose your personal information to third parties for their own direct marketing
              purposes as provided in Shine the Light. To opt out of future such disclosures, if
              applicable, please email us at{' '}
              <ControlLink
                className="PrivacyPolicyPage__link"
                href="mailto:privacy@modaoperandi.com"
              >
                privacy@modaoperandi.com
              </ControlLink>
              . If you want to opt out of activities that are considered “sales” or “sharing” under
              the California CCPA, please see the “Your Choices Regarding Sharing and Selling”
              section above.
            </Text>

            <Text treatment="h5">Retention of your information</Text>
            <Text treatment="body1">
              We retain information for different periods of time depending on the purposes for
              which we collect and use it, as described in this Privacy Policy. We will delete or
              de-identify information when it is no longer needed to fulfill these purposes unless a
              longer retention period is required to comply with applicable laws. There may be
              technical or other operational reasons where we are unable to fully delete or
              de-identify your information. Where this is the case, we will take reasonable measures
              to prevent further processing your information.
            </Text>
          </Stack>
          <Link to="#children" className="PrivacyPolicyPage__section-link">
            <Text id="children" treatment="h5">
              Children
            </Text>
          </Link>
          <Text treatment="body1">
            Our Services are intended for general audiences and are not directed at children. If we
            become aware that we have collected data without legally valid parental consent from
            children under an age where such consent is required, we will take reasonable steps to
            delete it as soon as practicable.
          </Text>

          <Link to="#international-users" className="PrivacyPolicyPage__section-link">
            <Text id="international-users" treatment="h5">
              Information for international users
            </Text>
          </Link>
          <Text treatment="body1">
            We operate globally and may transfer, store, and/or process your information to or with
            other entities within the Moda family of companies or other third parties such as
            trusted service providers and partners in locations around the world (including the
            United States) for the purposes described in this Privacy Policy. Wherever your
            information is transferred, stored or processed by us, we take reasonable steps to
            protect your information in accordance with this Privacy Policy and applicable laws.
            These measures may include implementing Standard Contractual Clauses to govern the
            transfer of your information, or other means recognized by applicable laws. By providing
            us with your information, you acknowledge any such transfer, storage, or processing.
          </Text>
          <Text treatment="body1">
            If you have any concerns or complaints about our data processing activities, we urge you
            to first try to resolve such issues directly with us. However, if applicable, you may
            make a complaint to the data protection supervisory authority in the country where you
            are based, or seek a remedy through local courts if you believe your rights have been
            breached.
          </Text>
          <Link to="#links" className="PrivacyPolicyPage__section-link">
            <Text id="links" treatment="h5">
              Links to Other Websites
            </Text>
          </Link>
          <Text treatment="body1">
            The Services may contain links to third-party websites. We have no control over the
            privacy practices or the content of any of our business partners, advertisers, sponsors,
            or other websites to which we provide links. As such, we are not responsible for the
            content or the privacy policies of those third-party websites. You should check the
            applicable third-party privacy policy and terms and conditions when visiting any other
            websites.
          </Text>

          <Link to="#how-we-protect-your-information" className="PrivacyPolicyPage__section-link">
            <Text id="how-we-protect-your-information" treatment="h5">
              How we Protect your Information
            </Text>
          </Link>
          <Text treatment="body1">
            We implement a variety of physical, administrative and technical security measures to
            protect your information from loss, misuse and unauthorized access, disclosure,
            alteration and destruction, taking into account the risks involved in processing and the
            nature of such data, and comply with applicable laws and regulations. However, no
            electronic storage solution or transmission of information via the Internet can be
            entirely secure, so please note that we cannot guarantee absolute security.
          </Text>
          <Link to="#privacy-policy-changes" className="PrivacyPolicyPage__section-link">
            <Text id="privacy-policy-changes" treatment="h5">
              Changes to Our Privacy Policy
            </Text>
          </Link>
          <Text treatment="body1">
            This Privacy Policy is effective as of the date stated at the top of this policy. We may
            change this Privacy Policy from time to time as our business practices and applicable
            law change, and will post any changes on this page. By accessing or using our Services
            after we make any such changes to this Privacy Policy, you are deemed to have accepted
            such changes. Please be aware that, to the extent permitted by applicable law, our use
            of information is governed by the Privacy Policy in effect at the time we use the
            information. Please refer back to this Privacy Policy on a regular basis.
          </Text>
          <Link to="#contact-us" className="PrivacyPolicyPage__section-link">
            <Text id="contact-us" treatment="h5">
              How to Contact Us
            </Text>
          </Link>
          <Text treatment="body1">
            If you wish to contact us about this Privacy Policy, you may reach us by email at{' '}
            <ControlLink className="PrivacyPolicyPage__link" href="mailto:privacy@modaoperandi.com">
              privacy@modaoperandi.com
            </ControlLink>{' '}
            with the subject line, "Privacy Policy." You can also reach us at 1 (800) 707-9898 or by
            postal mail at 34 34th Street, Building 6, Suite 4A, Brooklyn, NY 11232, Attention:
            Legal Department.
          </Text>
        </Stack>
      </div>
    </>
  );
};
